import { useQuery } from "@tanstack/react-query";
import { BASE_URL } from "utils/constant";

const MEDIA_CACHE_NAME = "sitemaster-snag-media-v1";
const MEDIA_CACHE_MAX_AGE = 1000 * 60 * 60 * 24 * 7;
const memoryUrlCache = new Map();

const getMediaUrl = (data) => data?.image || data?.file || null;

const cacheRequest = (key) =>
    new Request(`${window.location.origin}/__sitemaster_cache__/${encodeURIComponent(key)}`);

const openMediaCache = async () => {
    if (!("caches" in window)) return null;
    return caches.open(MEDIA_CACHE_NAME);
};

const readJsonCache = async (key) => {
    try {
        const cache = await openMediaCache();
        if (!cache) return null;
        const response = await cache.match(cacheRequest(key));
        if (!response) return null;
        const cachedAt = Number(response.headers.get("x-sm-cached-at") || 0);
        if (!cachedAt || Date.now() - cachedAt > MEDIA_CACHE_MAX_AGE) return null;
        return response.json();
    } catch (error) {
        return null;
    }
};

const writeJsonCache = async (key, data) => {
    try {
        const cache = await openMediaCache();
        if (!cache) return;
        await cache.put(
            cacheRequest(key),
            new Response(JSON.stringify(data), {
                headers: {
                    "content-type": "application/json",
                    "x-sm-cached-at": String(Date.now()),
                },
            }),
        );
    } catch (error) {
        // cache is opportunistic
    }
};

const readBlobCache = async (key) => {
    try {
        const cache = await openMediaCache();
        if (!cache) return null;
        const response = await cache.match(cacheRequest(key));
        if (!response) return null;
        const cachedAt = Number(response.headers.get("x-sm-cached-at") || 0);
        if (!cachedAt || Date.now() - cachedAt > MEDIA_CACHE_MAX_AGE) return null;
        return response.blob();
    } catch (error) {
        return null;
    }
};

const writeBlobCache = async (key, blob) => {
    try {
        if (!blob) return;
        const cache = await openMediaCache();
        if (!cache) return;
        await cache.put(
            cacheRequest(key),
            new Response(blob, {
                headers: {
                    "content-type": blob.type || "application/octet-stream",
                    "x-sm-cached-at": String(Date.now()),
                },
            }),
        );
    } catch (error) {
        // cache is opportunistic
    }
};

const objectUrlFromBlob = (key, blob) => {
    if (!blob) return null;
    if (memoryUrlCache.has(key)) return memoryUrlCache.get(key);
    const objectUrl = URL.createObjectURL(blob);
    memoryUrlCache.set(key, objectUrl);
    return objectUrl;
};

const withCachedBlobUrl = (key, data, blob) => {
    const objectUrl = objectUrlFromBlob(key, blob);
    if (!objectUrl) return data;
    return {
        ...data,
        image: data?.image ? objectUrl : data?.image,
        file: data?.file ? objectUrl : data?.file,
    };
};

export const getImageUrl = async (mediaId) => {
    if (!mediaId) return null;
    const metaKey = `snag:${mediaId}:meta`;
    const blobKey = `snag:${mediaId}:blob`;
    const cachedData = await readJsonCache(metaKey);
    if (cachedData) {
        const cachedBlob = await readBlobCache(blobKey);
        return withCachedBlobUrl(blobKey, cachedData, cachedBlob);
    }

    try {
        const accessToken = localStorage.getItem("access");
        const response = await fetch(`${BASE_URL}/core/api/snag/${mediaId}/`, {
            method: "GET",
            headers: {
                Authorization: `Bearer ${accessToken}`,
            },
        });
        if (response.ok) {
            const data = await response.json();
            const mediaData = data.data;
            const mediaUrl = getMediaUrl(mediaData);
            let blob = null;
            if (mediaUrl) {
                try {
                    const mediaResponse = await fetch(mediaUrl);
                    if (mediaResponse.ok) blob = await mediaResponse.blob();
                } catch (error) {
                    blob = null;
                }
            }
            await writeJsonCache(metaKey, mediaData);
            await writeBlobCache(blobKey, blob);
            return withCachedBlobUrl(blobKey, mediaData, blob);
        } else {
            console.error("Failed to fetch media URL:", response.statusText);
            return null;
        }
    } catch (error) {
        console.error("Error fetching media URL:", error);
        return null;
    }
};

export const useImageUrl = (mediaId) => {
    return useQuery({
        queryKey: ["imageUrl", mediaId],
        queryFn: () => getImageUrl(mediaId),
        enabled: !!mediaId,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
        retry: 2,
    });
};
